/**
 * TakeEditorModal.tsx
 *
 * Modal que envolve o TakeEditor do projeto externo,
 * integra com Supabase (salvar/carregar presets) e
 * aplica as mudanças no app real via TakeBridge.
 *
 * USO no Video.tsx — substitua o botão #btn-show-take-editor:
 *
 *   import { TakeEditorModal } from './TakeEditorModal'
 *
 *   // No componente VideoMenu, adicione state:
 *   const [editorOpen, setEditorOpen] = useState(false)
 *
 *   // Substitua o botão:
 *   <Button onClick={() => setEditorOpen(true)} ...>Editor</Button>
 *   {editorOpen && (
 *     <TakeEditorModal
 *       projectId="SEU_PROJECT_ID"
 *       onClose={() => setEditorOpen(false)}
 *     />
 *   )}
 */

'use client'

import { useEffect, useRef, useState, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { Save, Download, Upload, Trash2, ChevronDown, X, Check, Loader2, FolderOpen } from 'lucide-react'
import { TakeEditor } from './take-editor'          // componente do zip
import { Timeline } from './timeline'               // componente do zip
import { DEFAULT_PRESET } from '../../../lib/default-preset'
import { getTotalDuration, getTakeAtTime, isValidPreset } from '../../../lib/animation-engine'
import type { AnimationPreset, Take } from './take-types'
import { applyPreset, applyFrameLighting, setActivePreset } from './take-bridge'
import { PresetGallery } from './PresetGallery'
import { TakePreview } from './take-preview-engine'
import { savePreset, listPresets, deletePreset } from './supabase-presets'
import type { SavedPreset } from './supabase-presets'

// ─── Props ────────────────────────────────────────────────────────────────────

// Resoluções disponíveis (mesmas do Video.tsx)
const RESOLUTIONS = [
  { label: '900×600',          value: '900x600',    w: 900,  h: 600,  group: 'Paisagem'  },
  { label: '1280×720 (HD)',    value: '1280x720',   w: 1280, h: 720,  group: 'Paisagem'  },
  { label: '1920×1080 (FHD)', value: '1920x1080',  w: 1920, h: 1080, group: 'Paisagem'  },
  { label: '2560×1440 (2K)',  value: '2560x1440',  w: 2560, h: 1440, group: 'Paisagem'  },
  { label: '450×900',          value: '450x900',    w: 450,  h: 900,  group: 'Retrato'   },
  { label: '640×1280 (HD)',   value: '640x1280',   w: 640,  h: 1280, group: 'Retrato'   },
  { label: '960×1920 (FHD)', value: '960x1920',   w: 960,  h: 1920, group: 'Retrato'   },
  { label: '1280×2560 (2K)', value: '1280x2560',  w: 1280, h: 2560, group: 'Retrato'   },
  { label: '600×600',          value: '600x600',    w: 600,  h: 600,  group: 'Quadrado'  },
  { label: '720×720 (HD)',    value: '720x720',    w: 720,  h: 720,  group: 'Quadrado'  },
  { label: '1080×1080 (FHD)',value: '1080x1080',  w: 1080, h: 1080, group: 'Quadrado'  },
  { label: '1920×1920 (2K)', value: '1920x1920',  w: 1920, h: 1920, group: 'Quadrado'  },
]

interface TakeEditorModalProps {
  projectId: string
  onClose: () => void
  initialResolution?: string
  onApply?: (resolution: string) => void  // callback para sincronizar resolução com Video.tsx
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function clonePreset(p: AnimationPreset): AnimationPreset {
  return JSON.parse(JSON.stringify(p))
}

// ─── Componente principal ─────────────────────────────────────────────────────

export function TakeEditorModal({ projectId, onClose, onApply }: TakeEditorModalProps) {
  // ── Preset em edição ──────────────────────────────────────────────────────
  const [preset, setPreset] = useState<AnimationPreset>(() => {
    // Tenta carregar do localStorage como rascunho
    try {
      const raw = localStorage.getItem(`takePreset_${projectId}`)
      if (raw) {
        const p = JSON.parse(raw)
        if (isValidPreset(p)) return p
      }
    } catch { /* ignora */ }
    return clonePreset(DEFAULT_PRESET)
  })

  const [selectedId, setSelectedId] = useState<string>(preset.takes[0]?.id ?? '')
  const [resolution, setResolution] = useState<string>(() => {
    // Sincroniza com a resolução já selecionada no Video.tsx via window
    const w = window as any
    if (typeof w.getResolutionVideo === 'function') {
      const r = w.getResolutionVideo()
      return `${r.width}x${r.height}`
    }
    return '640x1280'
  })
  const [showResDropdown, setShowResDropdown] = useState(false)

  const activeRes = RESOLUTIONS.find(r => r.value === resolution) ?? RESOLUTIONS[5]
  const aspectRatio = activeRes.w / activeRes.h
  const selectedTake = preset.takes.find(t => t.id === selectedId) ?? preset.takes[0]

  // ── Playback preview ──────────────────────────────────────────────────────
  const [isPlaying, setIsPlaying] = useState(false)
  const [displayTime, setDisplayTime] = useState(0)
  const timeRef      = useRef(0)
  const playingRef   = useRef(false)
  const presetRef    = useRef(preset)
  const lastTickRef  = useRef(performance.now())
  const rafRef       = useRef(0)
  presetRef.current  = preset

  // ── Supabase ──────────────────────────────────────────────────────────────
  const [savedList, setSavedList]     = useState<SavedPreset[]>([])
  const [currentSavedId, setCurrentSavedId] = useState<string | undefined>()
  const [loadingList, setLoadingList] = useState(false)
  const [saving, setSaving]           = useState(false)
  const [saveMsg, setSaveMsg]         = useState('')
  const [showGallery, setShowGallery] = useState(false)

  const fileInputRef = useRef<HTMLInputElement>(null)
  const total = getTotalDuration(preset)

  // ── Carregar lista de presets do Supabase ─────────────────────────────────
  const fetchList = useCallback(async () => {
    setLoadingList(true)
    try {
      const list = await listPresets(projectId)
      setSavedList(list)
    } catch (e) {
      console.error('[TakeEditorModal] Erro ao carregar presets:', e)
    } finally {
      setLoadingList(false)
    }
  }, [projectId])

  useEffect(() => { fetchList() }, [fetchList])

  // ── Mover o canvas real (#viewer-3d) para dentro do slot do editor ────────
  const canvasSlotRef = useRef<HTMLDivElement>(null)
  const originalParentRef = useRef<{ parent: Element; nextSibling: Node | null } | null>(null)

  useEffect(() => {
    const canvas = document.getElementById('viewer-3d')
    const slot   = canvasSlotRef.current
    if (!canvas || !slot) return

    const w = window as any

    // ── Guarda estado original completo ──────────────────────────────────
    originalParentRef.current = {
      parent:      canvas.parentElement!,
      nextSibling: canvas.nextSibling,
    }

    // Salva estilos inline originais do canvas
    const origCanvasStyle = {
      width:    canvas.style.width,
      height:   canvas.style.height,
      display:  canvas.style.display,
      position: canvas.style.position,
    }

    // Salva tamanho original do renderer/câmera
    const origRenderer = w.renderer ? {
      width:  w.renderer.domElement.width,
      height: w.renderer.domElement.height,
    } : null
    const origCamera = w.camera ? {
      aspect: w.camera.aspect,
      fov:    w.camera.fov,
    } : null

    // Salva a função setupCamera original para re-chamar depois
    const origSetupCamera = w.setupCamera

    // ── Move o canvas para o slot ─────────────────────────────────────────
    slot.appendChild(canvas)
    canvas.style.width    = '100%'
    canvas.style.height   = '100%'
    canvas.style.display  = 'block'
    canvas.style.position = 'absolute'

    return () => {
      // Para o preview antes de restaurar
      if (TakePreview.isRunning) TakePreview.stop()

      // ── Restaura posição DOM ────────────────────────────────────────────
      const orig = originalParentRef.current
      if (orig && canvas) {
        canvas.style.width    = origCanvasStyle.width
        canvas.style.height   = origCanvasStyle.height
        canvas.style.display  = origCanvasStyle.display
        canvas.style.position = origCanvasStyle.position
        if (orig.nextSibling) {
          orig.parent.insertBefore(canvas, orig.nextSibling)
        } else {
          orig.parent.appendChild(canvas)
        }
      }

      // ── Restaura tamanho do renderer ────────────────────────────────────
      if (origRenderer && w.renderer) {
        w.renderer.setSize(origRenderer.width, origRenderer.height)
      }

      // ── Restaura câmera ─────────────────────────────────────────────────
      if (origCamera && w.camera) {
        w.camera.aspect = origCamera.aspect
        w.camera.fov    = origCamera.fov
        w.camera.updateProjectionMatrix()
      }

      // ── Re-chama setupCamera se disponível (restaura OrbitControls etc) ─
      if (typeof origSetupCamera === 'function') {
        requestAnimationFrame(() => origSetupCamera())
      }

      // Reativa controls
      if (w.controls) w.controls.enabled = true
    }
  }, [])

  // ── ResizeObserver: mantém renderer em sincronia com o slot visual ─────────
  // Não usamos setSize(res.w, res.h) porque isso deforma o canvas.
  // O renderer deve seguir o tamanho PIXEL do slot, e só o aspect ratio da
  // resolução selecionada determina a proporção do slot via CSS.
  useEffect(() => {
    const w = window as any
    const slot = canvasSlotRef.current
    if (!slot) return

    const syncRenderer = () => {
      const { width, height } = slot.getBoundingClientRect()
      if (width < 1 || height < 1) return
      if (w.renderer) {
        w.renderer.setSize(Math.round(width), Math.round(height), false)
        // false = não muda o style do canvas (o CSS já controla isso)
      }
      if (w.camera) {
        w.camera.aspect = activeRes.w / activeRes.h
        w.camera.updateProjectionMatrix()
      }
    }

    // Sincroniza imediatamente e depois observa redimensionamentos
    syncRenderer()
    const ro = new ResizeObserver(syncRenderer)
    ro.observe(slot)

    // Guarda aspect ratio para uso no Video.tsx
    if (typeof w.getResolutionVideo === 'function') {
      w._previewResolution = { width: activeRes.w, height: activeRes.h }
    }

    return () => ro.disconnect()
  }, [resolution, activeRes])

  // ── Preview via TakePreview engine (câmera real, sem gravar) ────────────
  // O engine roda no render() do editor via window.previewingTakes.
  // Aqui apenas registramos o callback onTick para atualizar a timeline UI.
  // O RAF interno é só para o caso do render loop não estar rodando (fallback).
  useEffect(() => {
    // Fallback RAF — atualiza UI quando não há render loop externo
    const fallbackLoop = () => {
      rafRef.current = requestAnimationFrame(fallbackLoop)
      if ((window as any).previewingTakes) {
        const t = TakePreview.time
        setDisplayTime(t)
        timeRef.current = t
      }
    }
    fallbackLoop()
    return () => {
      cancelAnimationFrame(rafRef.current)
      // Para o preview quando o modal fecha
      if (TakePreview.isRunning) TakePreview.stop()
    }
  }, [])

  // ── Scrub na timeline ─────────────────────────────────────────────────────
  const handleScrub = (t: number) => {
    timeRef.current = t
    setDisplayTime(t)
    // Aplica o frame imediatamente na cena real via seek
    TakePreview.seekTo(t)
  }

  const handleSelectTake = (id: string) => {
    setSelectedId(id)
    const take = preset.takes.find(t => t.id === id)
    if (take) {
      if (TakePreview.isRunning) TakePreview.stop()
      playingRef.current = false
      setIsPlaying(false)
      timeRef.current = take.startTime
      setDisplayTime(take.startTime)
      // Mostra o primeiro frame do take no canvas real
      TakePreview.seekTo(take.startTime)
    }
  }

  // ── Atualizar take ────────────────────────────────────────────────────────
  // Quando o preset muda, atualiza o engine e scruba para mostrar o frame atual
  const handlePresetChange = (newPreset: typeof preset) => {
    setPreset(newPreset)
    // Se o engine estava parado, aplica o frame atual com o novo preset
    if (!TakePreview.isRunning) {
      TakePreview.seekTo(timeRef.current)
    }
  }

  const updateTake = (updated: Take) => {
    setPreset(p => {
      const next = { ...p, takes: p.takes.map(t => t.id === updated.id ? updated : t) }
      // Atualiza o engine com o novo preset em tempo real
      if (!TakePreview.isRunning) {
        // pequeno timeout para o setState completar antes de buscar o frame
        setTimeout(() => TakePreview.seekTo(timeRef.current), 0)
      }
      return next
    })
  }

  const addTake = () => {
    const last = preset.takes[preset.takes.length - 1]
    const newTake: Take = {
      ...clonePreset({ ...DEFAULT_PRESET, takes: [last] }).takes[0],
      id:        `take-${Date.now()}`,
      name:      `Novo Take ${preset.takes.length + 1}`,
      startTime: last.endTime,
      endTime:   last.endTime + 6,
    }
    setPreset(p => ({ ...p, takes: [...p.takes, newTake] }))
    setSelectedId(newTake.id)
  }

  const deleteTake = (id: string) => {
    if (preset.takes.length <= 1) return
    setPreset(p => ({ ...p, takes: p.takes.filter(t => t.id !== id) }))
    if (selectedId === id) setSelectedId(preset.takes[0].id)
  }

  // ── Auto-salva rascunho no localStorage ───────────────────────────────────
  useEffect(() => {
    try {
      localStorage.setItem(`takePreset_${projectId}`, JSON.stringify(preset))
    } catch { /* quota */ }
  }, [preset, projectId])

  // ── Salvar no Supabase ────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true)
    try {
      const saved = await savePreset(projectId, preset, currentSavedId)
      setCurrentSavedId(saved.id)
      setSaveMsg('Salvo!')
      await fetchList()
    } catch (e: any) {
      setSaveMsg(`Erro: ${e.message ?? 'falha ao salvar'}`)
    } finally {
      setSaving(false)
      setTimeout(() => setSaveMsg(''), 3000)
    }
  }

  // ── Carregar preset do Supabase ───────────────────────────────────────────
  const handleLoad = (saved: SavedPreset) => {
    if (isValidPreset(saved.preset)) {
      setPreset(saved.preset)
      setCurrentSavedId(saved.id)
      setSelectedId(saved.preset.takes[0]?.id ?? '')
      setDisplayTime(0)
      timeRef.current = 0
      setShowGallery(false)
      setSaveMsg('Carregado!')
      setTimeout(() => setSaveMsg(''), 2000)
    }
  }

  const handleSelectBuiltin = (preset: AnimationPreset) => {
    setPreset(preset)
    setCurrentSavedId(undefined)
    setSelectedId(preset.takes[0]?.id ?? '')
    setDisplayTime(0)
    timeRef.current = 0
    TakePreview.seekTo(0)
    setShowGallery(false)
    setSaveMsg('Preset carregado!')
    setTimeout(() => setSaveMsg(''), 2000)
  }

  // ── Deletar preset do Supabase ────────────────────────────────────────────
  const handleDelete = async (id: string) => {
    try {
      await deletePreset(id)
      if (currentSavedId === id) setCurrentSavedId(undefined)
      await fetchList()
    } catch (e: any) {
      setSaveMsg(`Erro ao deletar: ${e.message}`)
      setTimeout(() => setSaveMsg(''), 3000)
    }
  }

  // ── Export / Import JSON ──────────────────────────────────────────────────
  const exportJson = () => {
    const blob = new Blob([JSON.stringify(preset, null, 2)], { type: 'application/json' })
    const url  = URL.createObjectURL(blob)
    const a    = document.createElement('a')
    a.href     = url
    a.download = `${preset.name.replace(/\s+/g, '-').toLowerCase()}.json`
    a.click()
    URL.revokeObjectURL(url)
  }

  const importJson = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => {
      try {
        const data = JSON.parse(reader.result as string)
        if (isValidPreset(data)) {
          setPreset(data)
          setSelectedId(data.takes[0]?.id ?? '')
          timeRef.current = 0
          setDisplayTime(0)
          setCurrentSavedId(undefined)
          setSaveMsg('Importado!')
        } else {
          setSaveMsg('JSON inválido')
        }
      } catch {
        setSaveMsg('Erro ao ler JSON')
      }
      setTimeout(() => setSaveMsg(''), 3000)
    }
    reader.readAsText(file)
    e.target.value = ''
  }

  // ── Aplicar preset no app real e fechar ───────────────────────────────────
  const handleApplyAndClose = () => {
    // Aplica no engine (atualiza takes + TOTAL_DURATION)
    setActivePreset(preset)
    applyPreset(preset)
    // Persiste no window para que o Download.js use na próxima geração de vídeo
    ;(window as any).activeTakePreset = preset
    // Sincroniza resolução de volta para o Video.tsx
    onApply?.(resolution)
    // Também usa o setter exposto no window (fallback)
    const w = window as any
    if (typeof w.setResolutionVideo === 'function') {
      w.setResolutionVideo(resolution)
    }
    onClose()
  }

  const currentTakeIndex = getTakeAtTime(preset, displayTime).index

  // ─────────────────────────────────────────────────────────────────────────
  const resGroups = ['Paisagem', 'Retrato', 'Quadrado']

  // Renderiza via portal no body para escapar de qualquer transform/filter pai
  // que quebraria o position:fixed dentro da sidebar
  const [portalTarget] = useState(() =>
    typeof document !== 'undefined' ? document.body : null
  )

  const editorContent = (
    <div style={{
      position: 'fixed', inset: 0,
      background: '#080c14',
      zIndex: 9999,
      display: 'flex', flexDirection: 'column',
      fontFamily: 'system-ui, -apple-system, sans-serif',
    }}>

      {/* ══ HEADER ══════════════════════════════════════════════════════════ */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '10px 16px', borderBottom: '1px solid #1e293b',
        background: '#0d1117', flexShrink: 0, gap: 10, flexWrap: 'wrap',
      }}>
        {/* Esquerda: título + status */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ color: '#e2e8f0', fontWeight: 700, fontSize: 14, letterSpacing: '-0.01em' }}>
            Editor de Takes
          </span>
          {currentSavedId && <Badge color="#4ade80" bg="#052e16" border="#166534">salvo</Badge>}
          {saveMsg      && <Badge color="#fbbf24" bg="#1c1007" border="#78350f">{saveMsg}</Badge>}
        </div>

        {/* Direita: ações */}
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>

          {/* Resolução */}
          <div style={{ position: 'relative' }}>
            <button onClick={() => setShowResDropdown(v => !v)} style={headerBtn('#1e293b')}>
              <span style={{ fontSize: 11 }}>{activeRes.label}</span>
              <ChevronDown size={11} />
            </button>
            {showResDropdown && (
              <div style={{
                position: 'absolute', top: '110%', right: 0,
                background: '#0f172a', border: '1px solid #1e293b',
                borderRadius: 8, minWidth: 200, zIndex: 200,
                boxShadow: '0 8px 30px rgba(0,0,0,0.7)', overflow: 'hidden',
              }}>
                {resGroups.map(group => (
                  <div key={group}>
                    <div style={{ padding: '7px 12px 4px', fontSize: 9, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.1em', borderTop: '1px solid #1e293b' }}>
                      {group}
                    </div>
                    {RESOLUTIONS.filter(r => r.group === group).map(r => (
                      <button key={r.value} onClick={() => { setResolution(r.value); setShowResDropdown(false) }}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          width: '100%', padding: '8px 14px', background: r.value === resolution ? '#1e293b' : 'transparent',
                          border: 'none', cursor: 'pointer', color: r.value === resolution ? '#e2e8f0' : '#94a3b8',
                          fontSize: 12, textAlign: 'left',
                        }}>
                        {r.label}
                        {r.value === resolution && <Check size={11} color="#6366f1" />}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Galeria de Presets */}
          <button onClick={() => setShowGallery(true)} style={headerBtn('#1e293b')} title="Presets de animação">
            <FolderOpen size={13} />
            <span style={{ fontSize: 11 }}>Presets</span>
          </button>

          <button onClick={handleSave} disabled={saving} style={headerBtn('#1e3a5f')} title="Salvar">
            {saving ? <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={13} color="#60a5fa" />}
            <span style={{ fontSize: 11 }}>{saving ? 'Salvando…' : 'Salvar'}</span>
          </button>
          <button onClick={exportJson} style={headerBtn('#1e293b')} title="Exportar JSON"><Download size={13} /></button>
          <button onClick={() => fileInputRef.current?.click()} style={headerBtn('#1e293b')} title="Importar JSON"><Upload size={13} /></button>

          {/* Aplicar e fechar */}
          <button onClick={handleApplyAndClose} style={{
            display: 'flex', alignItems: 'center', gap: 6,
            background: '#6366f1', border: 'none', borderRadius: 6,
            color: '#fff', fontSize: 12, fontWeight: 700,
            padding: '7px 16px', cursor: 'pointer',
          }}>
            <Check size={13} /> Aplicar e Fechar
          </button>

          <button onClick={onClose} style={headerBtn('#2d1b1b')} title="Cancelar sem salvar">
            <X size={14} color="#f87171" />
          </button>
        </div>
      </div>

      {/* ══ BODY ════════════════════════════════════════════════════════════ */}
      <div style={{ flex: 1, display: 'flex', minHeight: 0, overflow: 'hidden' }}>

        {/* ── Sidebar esquerda: lista de takes ─────────────────────────── */}
        <div style={{
          width: 200, flexShrink: 0,
          borderRight: '1px solid #1e293b',
          display: 'flex', flexDirection: 'column',
          background: '#0a0f1a',
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '8px 12px', borderBottom: '1px solid #1e293b',
          }}>
            <span style={{ fontSize: 10, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Takes</span>
            <button onClick={addTake} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6366f1', fontSize: 20, lineHeight: 1, padding: '0 2px' }} title="Novo take">+</button>
          </div>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {preset.takes.map((take, i) => (
              <div key={take.id} onClick={() => handleSelectTake(take.id)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '10px 12px', cursor: 'pointer',
                  background: take.id === selectedId ? '#1e293b' : i === currentTakeIndex ? '#0f172a' : 'transparent',
                  borderLeft: `3px solid ${take.id === selectedId ? '#6366f1' : 'transparent'}`,
                  borderBottom: '1px solid #0d1117',
                  transition: 'background 0.15s',
                }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{
                    fontSize: 12, fontWeight: 600,
                    color: take.id === selectedId ? '#e2e8f0' : '#94a3b8',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>{take.name}</div>
                  <div style={{ fontSize: 9, color: '#334155', fontFamily: 'monospace', marginTop: 2 }}>
                    {take.startTime}s – {take.endTime}s
                  </div>
                </div>
                {preset.takes.length > 1 && (
                  <button onClick={e => { e.stopPropagation(); deleteTake(take.id) }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#334155', padding: 3, opacity: 0, transition: 'opacity 0.1s' }}
                    onMouseEnter={e => (e.currentTarget.style.opacity = '1')}
                    onMouseLeave={e => (e.currentTarget.style.opacity = '0')}
                  >
                    <Trash2 size={11} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* ── Centro: canvas 3D com proporção correta ──────────────────── */}
        <div style={{
          flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: '#05080f', position: 'relative', overflow: 'hidden',
          minWidth: 0,
        }}>
          {/* Slot que recebe o canvas #viewer-3d via portal DOM */}
          <div
            ref={canvasSlotRef}
            style={{
              // Estratégia: sempre ancoramos pela altura do container.
              // height: 100% + aspect-ratio deixa o browser calcular a largura.
              // maxWidth: 100% impede que ultrapasse o container em landscape.
              aspectRatio: `${activeRes.w} / ${activeRes.h}`,
              height: '100%',
              width: 'auto',
              maxWidth: '100%',
              background: '#000',
              position: 'relative',
              borderRadius: 4,
              overflow: 'hidden',
              boxShadow: '0 0 0 1px #1e293b, 0 20px 60px rgba(0,0,0,0.6)',
            }}
          />

          {/* Overlay: resolução atual */}
          <div style={{
            position: 'absolute', top: 10, left: 10,
            background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
            borderRadius: 6, padding: '4px 10px',
            fontSize: 11, color: '#94a3b8', fontFamily: 'monospace',
            pointerEvents: 'none',
          }}>
            {activeRes.w}×{activeRes.h}
          </div>

          {/* Overlay: timecode */}
          <div style={{
            position: 'absolute', top: 10, right: 10,
            background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
            borderRadius: 6, padding: '4px 10px',
            fontSize: 11, color: '#e2e8f0', fontFamily: 'monospace',
            pointerEvents: 'none',
          }}>
            {displayTime.toFixed(2)}s
            <span style={{ color: '#475569' }}> / {total.toFixed(1)}s</span>
          </div>
        </div>

        {/* ── Sidebar direita: editor do take selecionado ──────────────── */}
        <div style={{
          width: 300, flexShrink: 0,
          borderLeft: '1px solid #1e293b',
          display: 'flex', flexDirection: 'column',
          background: '#0a0f1a', overflow: 'hidden',
        }}>
          <div style={{ padding: '8px 14px', borderBottom: '1px solid #1e293b', flexShrink: 0 }}>
            <span style={{ fontSize: 10, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
              Editando: <span style={{ color: '#818cf8' }}>{selectedTake?.name}</span>
            </span>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px' }}>
            {selectedTake && (
              <TakeEditor
                take={selectedTake}
                onChange={updateTake}
                onGoToStart={() => {
                  // Move câmera para posição inicial do take
                  TakePreview.seekTo(selectedTake.startTime)
                  timeRef.current = selectedTake.startTime
                  setDisplayTime(selectedTake.startTime)
                }}
                onGoToEnd={() => {
                  // Move câmera para posição final do take
                  // Usa um instante antes do fim para ver a posição final interpolada
                  const t = Math.max(selectedTake.startTime, selectedTake.endTime - 0.05)
                  TakePreview.seekTo(t)
                  timeRef.current = t
                  setDisplayTime(t)
                }}
                onCaptureStart={() => {
                  const w = window as any
                  if (!w.camera) return
                  updateTake({
                    ...selectedTake,
                    camera: {
                      ...selectedTake.camera,
                      start: {
                        position: {
                          x: Math.round(w.camera.position.x * 100) / 100,
                          y: Math.round(w.camera.position.y * 100) / 100,
                          z: Math.round(w.camera.position.z * 100) / 100,
                        },
                        lookAt: selectedTake.camera.start.lookAt,
                      },
                      fovStart: Math.round(w.camera.fov),
                    },
                  })
                }}
                onCaptureEnd={() => {
                  const w = window as any
                  if (!w.camera) return
                  updateTake({
                    ...selectedTake,
                    camera: {
                      ...selectedTake.camera,
                      end: {
                        position: {
                          x: Math.round(w.camera.position.x * 100) / 100,
                          y: Math.round(w.camera.position.y * 100) / 100,
                          z: Math.round(w.camera.position.z * 100) / 100,
                        },
                        lookAt: selectedTake.camera.end.lookAt,
                      },
                      fovEnd: Math.round(w.camera.fov),
                    },
                  })
                }}
              />
            )}
          </div>
        </div>
      </div>

      {/* ══ FOOTER: transport + timeline ════════════════════════════════════ */}
      <div style={{
        borderTop: '1px solid #1e293b', padding: '10px 16px',
        background: '#0d1117', flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>

          {/* Play / Pause */}
          <button
            onClick={() => {
              const wasPlaying = TakePreview.isRunning
              if (wasPlaying) {
                TakePreview.stop()
                playingRef.current = false
                setIsPlaying(false)
              } else {
                const fromTime = timeRef.current >= total ? 0 : timeRef.current
                TakePreview.start(
                  {
                    preset: presetRef.current,
                    onTick: (t) => { timeRef.current = t; setDisplayTime(t) },
                    onEnd:  () => { playingRef.current = false; setIsPlaying(false) },
                  },
                  fromTime,
                )
                playingRef.current = true
                setIsPlaying(true)
              }
            }}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: isPlaying ? '#1e293b' : '#6366f1',
              border: `1px solid ${isPlaying ? '#334155' : '#6366f1'}`,
              borderRadius: 6, color: '#fff', fontSize: 12, fontWeight: 600,
              padding: '6px 14px', cursor: 'pointer',
            }}
          >
            {isPlaying ? '⏸ Pausar' : '▶ Preview'}
          </button>

          {/* Reset */}
          <button
            onClick={() => {
              TakePreview.stop()
              TakePreview.seekTo(0)
              timeRef.current = 0
              setDisplayTime(0)
              playingRef.current = false
              setIsPlaying(false)
            }}
            style={{ ...headerBtn('#1e293b'), padding: '6px 10px', fontSize: 13 }}
          >
            ↺
          </button>

          <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#475569', minWidth: 90 }}>
            {displayTime.toFixed(2)}s / {total.toFixed(1)}s
          </span>

          {/* Take ativo na timeline */}
          <span style={{
            fontSize: 11, color: '#818cf8',
            background: '#1e1e3f', border: '1px solid #312e81',
            padding: '3px 10px', borderRadius: 20,
          }}>
            {preset.takes[currentTakeIndex]?.name ?? '—'}
          </span>
        </div>

        <Timeline
          preset={preset}
          currentTime={displayTime}
          selectedId={selectedId}
          onScrub={handleScrub}
          onSelect={handleSelectTake}
        />
      </div>

      {showGallery && (
        <PresetGallery
          savedPresets={savedList}
          onSelectBuiltin={handleSelectBuiltin}
          onSelectSaved={handleLoad}
          onDeleteSaved={handleDelete}
          onClose={() => setShowGallery(false)}
        />
      )}
      <input ref={fileInputRef} type="file" accept=".json" style={{ display: 'none' }} onChange={importJson} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )

  return portalTarget ? createPortal(editorContent, portalTarget) : null
}

// ─── Helpers inline ───────────────────────────────────────────────────────────

function Badge({ color, bg, border, children }: { color: string; bg: string; border: string; children: React.ReactNode }) {
  return (
    <span style={{ fontSize: 10, color, background: bg, border: `1px solid ${border}`, padding: '2px 8px', borderRadius: 20 }}>
      {children}
    </span>
  )
}




// ─── Micro-style helper ───────────────────────────────────────────────────────

function headerBtn(bg: string): React.CSSProperties {
  return {
    background: bg, border: '1px solid #334155', borderRadius: 5,
    color: '#94a3b8', cursor: 'pointer', padding: '5px 8px',
    display: 'flex', alignItems: 'center', gap: 5,
  }
}